import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Mic,
  ListOrdered,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { useTestSession } from '@/contexts/useTestSession';
import { logger } from '@/lib/logger';
import { track } from '@/lib/analytics';
import Button from '@/components/ui/Button';

export default function IntroPage() {
  const navigate = useNavigate();
  const { status, error, startTest, clearError } = useTestSession();
  const isLoading = status === 'loading';

  console.log('status', status, error, startTest);

  const handleBeginTest = useCallback(async () => {
    try{
      const success = await startTest();
      console.log('start test result', success);
          if (success) {
              console.log('success');
      track('mock_test_started');
      navigate('/interview/question', { replace: true });
    }
    } catch (err) {
      logger.error('Error starting test', err);
    }
    console.log('successs handle begin test');

  }, [startTest, navigate]);

  return (
    <div className="relative min-h-screen flex flex-col bg-cream md:bg-gradient-to-br md:from-cream md:via-cream md:to-cream-warm overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[700px] hidden md:block"
        style={{
          background:
            'radial-gradient(ellipse 900px 500px at 20% 10%, rgba(212,163,115,0.18), transparent 60%)',
        }}
      />

      <TopBar onBack={() => navigate(-1)} />

      <div className="relative flex-1 flex items-center justify-center px-6 py-8 md:px-8 md:py-12 lg:py-16">
        <div className="w-full max-w-3xl bg-card border border-border rounded-2xl shadow-sm p-6 md:p-10 lg:p-12">
          <AnimatePresence mode="wait" initial={false}>
            {isLoading ? (
              <motion.div
                key="loading"
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <LoadingContent />
              </motion.div>
            ) : (
              <motion.div
                key="intro"
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <IntroContent
                  onBegin={handleBeginTest}
                  isLoading={isLoading}
                  error={error}
                  onClearError={clearError}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function IntroContent({
  onBegin,
  isLoading,
  error,
  onClearError,
}: {
  onBegin: () => void;
  isLoading: boolean;
  error: { title: string; message: string; retryable?: boolean } | null;
  onClearError: () => void;
}) {
  return (
    <>
      <div className="flex items-center gap-3 mb-6">
        <div className="relative">
          <div
            aria-hidden
            className="absolute -inset-3 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(139,111,71,0.18) 0%, transparent 70%)',
            }}
          />
          <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shadow-md">
            <Mic size={26} className="text-white md:hidden" />
            <Mic size={30} className="text-white hidden md:block" />
          </div>
        </div>
        <span className="font-sans text-xs uppercase tracking-[0.18em] text-text-muted">
          Voice or Text
        </span>
      </div>

      <h1 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl leading-tight text-text-primary mb-3">
        Ready to Begin?
      </h1>
      <p className="font-sans text-base lg:text-lg text-text-secondary max-w-xl mb-6">
        You&apos;ll answer 20 civics questions, just like the real USCIS
        interview. Speak or type — we&apos;ll grade as you go.
      </p>

      <div className="flex flex-wrap gap-2 mb-8">
        <StatPill
          icon={<ListOrdered size={14} className="text-primary" />}
          label="20 Questions"
        />
        <StatPill
          icon={<CheckCircle size={14} className="text-success" />}
          label="60% to pass"
        />
        <StatPill
          icon={<Clock size={14} className="text-warning" />}
          label="~10–15 min"
        />
      </div>

      <div className="h-px bg-border mb-8" />

      <h3 className="font-display font-semibold text-lg md:text-xl text-text-primary mb-5">
        How it works
      </h3>
      <div className="flex flex-col gap-5 mb-8">
        <Step
          n={1}
          text="Listen to or read each civics question"
          detail="Tap the speaker icon to hear the question read aloud."
        />
        <Step
          n={2}
          text="Answer by speaking or typing your response"
          detail="Switch input mode anytime — we transcribe voice with Deepgram."
        />
        <Step
          n={3}
          text="Get instant results with detailed feedback"
          detail="See category breakdowns and the answers you missed."
        />
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-error/30 bg-error-light p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-sans text-sm font-semibold text-error mb-1">
                {error.title}
              </p>
              <p className="font-sans text-sm text-text-secondary">
                {error.message}
              </p>
              {error.retryable && (
                <button
                  onClick={onClearError}
                  className="font-sans text-sm font-medium text-primary mt-2 cursor-pointer"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="h-px bg-border mb-6" />

      <div className="md:flex md:justify-end">
        <div className="md:min-w-[220px]">
          <Button fullWidth onClick={onBegin} loading={isLoading}>
            Begin Test
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </>
  );
}

const LOADING_STAGES = [
  'Loading civics questions',
  'Calibrating the AI officer',
  'Setting the scene',
  'Almost ready',
];

const LOADING_TIPS = [
  'Tip — Speak naturally; the officer understands accents clearly.',
  'Tip — Short, confident answers score higher than long ones.',
  'Tip — You can switch between voice and typing anytime.',
  'Tip — There are no trick questions — answer as yourself.',
];

function LoadingContent() {
  const [stageIndex, setStageIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStageIndex((i) => (i + 1) % LOADING_STAGES.length);
    }, 1400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % LOADING_TIPS.length);
    }, 3600);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex flex-col items-center text-center py-10 md:py-14">
      <Spinner />

      <div className="h-9 md:h-10 mt-8 mb-2 flex items-center">
        <AnimatePresence mode="wait">
          <motion.h2
            key={stageIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-semibold text-xl md:text-2xl text-text-primary"
          >
            {LOADING_STAGES[stageIndex]}
            <DotsEllipsis />
          </motion.h2>
        </AnimatePresence>
      </div>

      <p className="font-sans text-sm text-text-muted max-w-sm">
        This only takes a moment.
      </p>

      <div className="mt-10 w-24 h-px bg-border" />

      <div className="mt-6 min-h-[44px] max-w-sm flex items-start">
        <AnimatePresence mode="wait">
          <motion.p
            key={tipIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="font-sans text-sm text-text-secondary italic leading-relaxed"
          >
            {LOADING_TIPS[tipIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div aria-hidden className="relative w-20 h-20 md:w-24 md:h-24">
      <svg
        viewBox="0 0 96 96"
        className="w-full h-full animate-spin"
        style={{ animationDuration: '2s', animationTimingFunction: 'linear' }}
      >
        <circle
          cx="48"
          cy="48"
          r="42"
          stroke="var(--color-border)"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="48"
          cy="48"
          r="42"
          stroke="var(--color-primary)"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          style={{
            animation: 'spinner-dash 1.5s ease-in-out infinite',
            transformOrigin: 'center',
          }}
        />
      </svg>
    </div>
  );
}

function DotsEllipsis() {
  return (
    <span aria-hidden className="inline-flex ml-1">
      {[0, 0.15, 0.3].map((delay) => (
        <motion.span
          key={delay}
          className="inline-block"
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', delay }}
        >
          .
        </motion.span>
      ))}
    </span>
  );
}

function TopBar({ onBack }: { onBack: () => void }) {
  return (
    <div className="relative z-10 border-b border-border/60 bg-card/40 backdrop-blur-sm">
      <div className="mx-auto w-full max-w-6xl px-6 md:px-8 lg:px-12 h-14 flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 font-sans text-sm text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted">
          Mock Interview
        </p>
        <span aria-hidden className="w-[52px]" />
      </div>
    </div>
  );
}

function StatPill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-cream/60 border border-border rounded-full px-3 py-1.5">
      {icon}
      <span className="font-sans text-xs font-medium text-text-primary">
        {label}
      </span>
    </div>
  );
}

function Step({ n, text, detail }: { n: number; text: string; detail: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
        <span className="font-sans text-sm font-bold text-white">{n}</span>
      </div>
      <div className="pt-0.5">
        <p className="font-sans text-sm font-medium text-text-primary leading-snug">
          {text}
        </p>
        <p className="font-sans text-sm text-text-secondary leading-snug mt-1">
          {detail}
        </p>
      </div>
    </div>
  );
}
